import { useState, useEffect, useRef } from 'react';
import cn from 'classnames';

import useOutsideClick from 'js/hooks/use-outside-click';

import Button from 'components/button/Button';
import ContentContainer from 'components/content-container/ContentContainer';
import GlobalSearch from './GlobalSearch';
import Link from 'components/link/Link';
import Menu from 'components/menu/Menu';
import TabMenu from 'components/tab-menu/TabMenu';
import SimpleHeader from './SimpleHeader';

import { IHeader } from 'source/NFR.types';

import logoIcon from '../../storybook/assets/logo.svg';

const Header: React.FC<IHeader> = ({
  globalSearch,
  linkToHome,
  linkList = [],
  logo = logoIcon,
  menu,
  menuText,
  tabMenu,
  priorityLink,
  simpleHeader
}) => {
  const [globalSearchIsOpen, setGlobalSearchIsOpen] = useState<boolean>();
  const [isMounted, setIsMounted] = useState<boolean>();
  const [menuIsOpen, setMenuIsOpen] = useState<boolean>();

  const containerRef = useRef<HTMLElement>(null);

  const openGlobalSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setGlobalSearchIsOpen(true);
    setMenuIsOpen(false);
  };

  const closeGlobalSearch = () => {
    setGlobalSearchIsOpen(false);
  };

  const openMenu = () => {
    document.body.style.position = 'fixed';
    setMenuIsOpen(true);
    setGlobalSearchIsOpen(false);
  };

  const closeMenu = () => {
    document.body.style.position = '';
    setMenuIsOpen(false);
  };

  useOutsideClick(containerRef, closeGlobalSearch);

  useEffect(() => setIsMounted(true), []);

  const menuId = 'main-menu';
  const menuLinkList = priorityLink ? [priorityLink, ...linkList] : linkList;

  return (
    <>
      <TabMenu openMenu={openMenu} {...tabMenu} />
      {simpleHeader && <SimpleHeader {...simpleHeader} />}
      <header className="header" ref={containerRef}>
        <ContentContainer element="nav" className="header--content" theme="wide">
          <a href={linkToHome.url} className="header--logo">
            <span>{linkToHome.text}</span>
            <img alt="Logo" height="38" width="200" src={logo} />
          </a>
          <ul
            className={cn('header--nav', {
              '-menu-is-open': menuIsOpen
            })}
          >
            {linkList.map(link => (
              <li key={link.url} className="header--link">
                <Link openInNewWindow={link.isExternal} {...link} />
              </li>
            ))}
            {priorityLink && (
              <li key={priorityLink.url} className="header--link header--link--show-on-mobile">
                <Link openInNewWindow={priorityLink.isExternal} {...priorityLink} />
              </li>
            )}
            <li className="header--search">
              {globalSearch?.input && (
                <GlobalSearch
                  closeGlobalSearch={closeGlobalSearch}
                  isOpen={globalSearchIsOpen}
                  openGlobalSearch={openGlobalSearch}
                  {...globalSearch}
                />
              )}
            </li>
            <li className="header--menu">
              <Button
                className="header--menu-button"
                theme="primary"
                onClick={openMenu}
                aria-controls={isMounted ? menuId : undefined}
                aria-expanded={menuIsOpen}
              >
                {menuText}
              </Button>
            </li>
          </ul>

          <Menu
            closeMenu={closeMenu}
            globalSearch={globalSearch}
            isOpen={menuIsOpen}
            {...menu}
            id={menuId}
            headerLinkList={menuLinkList}
          />
        </ContentContainer>
      </header>
    </>
  );
};

Header.displayName = 'Header';

export default Header;
